import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

function BannerModal({ banner, onClose, onSaved }: { banner?: any; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!banner;
  const [form, setForm] = useState({
    title: banner?.title || '',
    subtitle: banner?.subtitle || '',
    description: banner?.description || '',
    button_text: banner?.button_text || '',
    button_link: banner?.button_link || '',
    text_color: banner?.text_color || 'white',
    position: banner?.position || 'hero',
    sort_order: banner?.sort_order || 0,
    is_active: banner?.is_active !== false,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, typeof v === 'boolean' ? (v ? '1' : '0') : String(v)));
      if (imageFile) formData.append('image', imageFile);
      if (isEdit) {
        formData.append('_method', 'PUT');
        await api.post(`/admin/banners/${banner.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Banner updated!');
      } else {
        await api.post('/admin/banners', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Banner created!');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        (Object.values(errors) as string[][]).flat().forEach(msg => toast.error(msg));
      } else {
        toast.error(err.response?.data?.message || 'Failed to save');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 overflow-y-auto py-8">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Banner' : 'Add Banner'}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="Banner headline" />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Subtitle</label>
            <input value={form.subtitle} onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="Secondary text" />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Button Text</label>
              <input value={form.button_text} onChange={e => setForm(p => ({ ...p, button_text: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="Shop Now" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Button Link</label>
              <input value={form.button_link} onChange={e => setForm(p => ({ ...p, button_link: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="/men" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Position</label>
              <select value={form.position} onChange={e => setForm(p => ({ ...p, position: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 bg-white">
                <option value="hero">Hero</option>
                <option value="middle">Middle</option>
                <option value="sidebar">Sidebar</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Text Color</label>
              <select value={form.text_color} onChange={e => setForm(p => ({ ...p, text_color: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 bg-white">
                <option value="white">White</option>
                <option value="black">Black</option>
                <option value="gray">Gray</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Sort Order</label>
            <input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: Number(e.target.value) }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-2">Banner Image</label>
            {banner?.image && !imageFile && (
              <img src={getImageUrl(banner.image)} alt="Current" className="h-20 object-cover rounded mb-2" />
            )}
            <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] || null)} className="text-sm text-gray-600" />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-sm font-medium hover:border-gray-900 transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="px-8 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-60">
              {isEdit ? 'Save Changes' : 'Create Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminBanners() {
  const [banners, setBanners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editBanner, setEditBanner] = useState<any>(null);

  const fetchBanners = () => {
    setLoading(true);
    api.get('/admin/banners').then(r => setBanners(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchBanners(); }, []);

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`Delete banner "${title}"?`)) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      toast.success('Banner deleted');
      fetchBanners();
    } catch { toast.error('Failed to delete banner'); }
  };

  const toggleActive = async (banner: any) => {
    try {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      formData.append('is_active', banner.is_active ? '0' : '1');
      formData.append('title', banner.title);
      await api.post(`/admin/banners/${banner.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      fetchBanners();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
        <button onClick={() => { setEditBanner(null); setModalOpen(true); }} className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-gray-700 transition-colors">
          <Plus size={16} /> Add Banner
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Banner</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Position</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Button</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sort</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? Array(3).fill(null).map((_, i) => (
              <tr key={i}><td colSpan={6} className="px-6 py-4"><div className="h-16 bg-gray-100 animate-pulse rounded" /></td></tr>
            )) : banners.map(banner => (
              <tr key={banner.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-28 h-18 flex-shrink-0 rounded overflow-hidden bg-gray-100 border border-gray-200">
                      {banner.image
                        ? <img src={getImageUrl(banner.image)} alt={banner.title} className="w-28 h-18 object-cover" style={{ height: '72px' }} />
                        : <div className="w-28 flex items-center justify-center text-gray-300 text-xs" style={{ height: '72px' }}>No Image</div>
                      }
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{banner.title}</p>
                      {banner.subtitle && <p className="text-xs text-gray-400">{banner.subtitle}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-xs font-medium px-2 py-1 bg-gray-100 rounded capitalize">{banner.position}</span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {banner.button_text ? (
                    <div>
                      <p className="text-xs font-medium">{banner.button_text}</p>
                      <p className="text-xs text-gray-400">{banner.button_link}</p>
                    </div>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{banner.sort_order}</td>
                <td className="px-6 py-4">
                  <button onClick={() => toggleActive(banner)} className={`text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${banner.is_active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}>
                    {banner.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => { setEditBanner(banner); setModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"><Edit size={15} /></button>
                    <button onClick={() => handleDelete(banner.id, banner.title)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && banners.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm">No banners yet. Add your first banner.</div>
        )}
      </div>

      {modalOpen && (
        <BannerModal
          banner={editBanner}
          onClose={() => { setModalOpen(false); setEditBanner(null); }}
          onSaved={fetchBanners}
        />
      )}
    </div>
  );
}
